import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronDown, ChevronUp, ClipboardList, Loader2, Truck } from "lucide-react";
import { Card } from "@/components/ui/card";
import LocationHeader from "@/components/LocationHeader";
import LocationAccessGate from "@/components/LocationAccessGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  pending: "bg-gray-200 text-gray-700",
  confirmed: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
};

export default function LocationOrdersPage() {
  const { slug } = useParams();
  const [locationInfo, setLocationInfo] = useState(null);
  const [accessCode, setAccessCode] = useState(() =>
    localStorage.getItem(`munchd-loc-code:${slug}`)
  );
  const [verifying, setVerifying] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/locations/${slug}/info`)
      .then((res) => {
        if (cancelled) return;
        setLocationInfo(res.data);
        if (res.data.mode !== "weekly_order") setNotAllowed(true);
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setVerifying(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const loadOrders = useCallback(async () => {
    if (!accessCode) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/locations/${slug}/orders?limit=50`, {
        headers: { "X-Location-Code": accessCode },
      });
      setOrders(res.data.orders || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(`munchd-loc-code:${slug}`);
        setAccessCode(null);
      }
    } finally {
      setLoading(false);
    }
  }, [accessCode, slug]);

  useEffect(() => {
    if (accessCode && locationInfo && !notAllowed) loadOrders();
  }, [accessCode, locationInfo, notAllowed, loadOrders]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#1aa59a]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Location not found</h1>
        </div>
      </div>
    );
  }

  if (notAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Order history unavailable</h1>
          <p className="text-gray-500 mb-4">
            This location isn&apos;t set up for ordering. Ask your admin to switch it to Weekly Order mode.
          </p>
          <Link to={`/loc/${slug}`} className="text-[#1aa59a] font-semibold hover:underline">
            ← Back to menu
          </Link>
        </div>
      </div>
    );
  }

  if (!accessCode) {
    return (
      <LocationAccessGate
        slug={slug}
        locationInfo={locationInfo}
        onVerified={(code) => setAccessCode(code)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12" data-testid="location-orders-page">
      <LocationHeader
        location={locationInfo}
        showOrdersLink={false}
        slug={slug}
      />

      <div className="max-w-7xl mx-auto px-4 pt-4">
        <Link
          to={`/loc/${slug}`}
          className="inline-flex items-center text-sm text-gray-600 hover:text-[#1aa59a]"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to menu
        </Link>
      </div>

      <main className="max-w-7xl mx-auto px-4 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="w-6 h-6 text-[#1aa59a]" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Order History</h2>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1aa59a]" />
          </div>
        ) : orders.length === 0 ? (
          <Card className="p-8 text-center" data-testid="orders-empty">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 font-semibold">No orders yet</p>
            <p className="text-sm text-gray-400">Once you place an order it will appear here.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const isOpen = expandedId === o.id;
              return (
                <Card key={o.id} className="overflow-hidden" data-testid={`order-row-${o.order_number}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : o.id)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-gray-800">
                          {o.order_number || o.id.slice(0, 8)}
                        </span>
                        <span
                          className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                            STATUS_STYLE[o.status] || STATUS_STYLE.pending
                          }`}
                        >
                          {o.status || "pending"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        Placed {new Date(o.created_at).toLocaleDateString()} · Delivery {o.delivery_date}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-[#1aa59a]">£{Number(o.total_amount || 0).toFixed(2)}</div>
                      <div className="text-[11px] text-gray-500">{(o.items || []).length} item(s)</div>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>

                  {isOpen && (
                    <div className="p-4 border-t bg-slate-50 text-sm space-y-3">
                      {(o.delivery_groups || []).length > 0 ? (
                        (o.delivery_groups || []).map((g, idx) => (
                          <div key={`${o.id}-g${idx}`} className="bg-white border rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Truck className="w-3.5 h-3.5 text-[#1aa59a]" />
                              <span className="text-xs font-bold uppercase text-gray-700">
                                Delivery {idx + 1}
                              </span>
                              <span className="text-xs text-gray-500">
                                · {g.date || g.day_hint || "TBC"}
                              </span>
                            </div>
                            <ul className="text-xs text-gray-700 space-y-0.5">
                              {(o.items || [])
                                .filter((it) => (g.item_ids || []).includes(it.item_id))
                                .map((it, i) => (
                                  <li key={`${o.id}-g${idx}-i${i}`}>
                                    {it.quantity}× {it.name}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        ))
                      ) : (
                        <ul className="text-xs text-gray-700 space-y-0.5">
                          {(o.items || []).map((it, i) => (
                            <li key={`${o.id}-i${i}`}>
                              {it.quantity}× {it.name} — £{Number(it.price || 0).toFixed(2)}
                            </li>
                          ))}
                        </ul>
                      )}

                      {o.notes && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
                          <strong>Notes:</strong> {o.notes}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
